// @spec INC-STR-001
// @spec INC-STR-002
// @spec INC-STR-039
// @spec INC-STR-040
'use client';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { FilePlus2, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RiskSecurityModuleContext } from '@/components/risk-security-module-context';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DEFAULT_INCIDENT_FILTERS,
  IncidentStatusBadge,
  useIncidents,
  useQuickReports,
} from '@/features/incidents';
import { CirtFormChooserDialog } from '@/features/incidents/components/CirtFormChooserDialog';
import { useNIS2IncidentFlag } from '@/features/incidents/nis2/api/nis2-incident-flag';
import type { IncidentListItem, QuickReport } from '@/features/incidents';
import { getReportedCirtForm } from '@/features/incidents/utils/cirt-email';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/incidents/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.incidents'),
  }),
  component: IncidentsPanelPage,
});

const DEFAULT_PAGE_SIZE = 12;

function formatDateTime(value: string | null | undefined) {
  if (!value) return '-';
  return new Date(value).toLocaleString();
}

function truncate(text: string, maxLength = 140) {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1)}…`;
}

function resolveReporterName(incident: IncidentListItem, t: (key: string) => string) {
  return incident.reporter?.displayName ?? t('panel.unassigned');
}

function derivePotentialIncidentName(report: QuickReport, t: (key: string) => string) {
  const compact = report.description.replace(/\s+/g, ' ').trim();
  if (!compact) {
    return t('panel.potentialIncident');
  }

  const sentence = compact.split(/[.!?]/)[0]?.trim() ?? compact;
  return truncate(sentence, 64);
}

function normalizePotentialIncidentStatus(status: string, t: (key: string) => string) {
  const normalized = status.trim().toLowerCase();

  if (normalized === 'submitted' || normalized === 'new') {
    return { label: t('panel.statusNew'), variant: 'default' as const };
  }

  if (normalized === 'under_review' || normalized === 'reviewing' || normalized === 'triage') {
    return { label: t('panel.statusUnderReview'), variant: 'outline' as const };
  }

  if (normalized === 'accepted' || normalized === 'converted') {
    return { label: t('panel.statusMovedToInvestigation'), variant: 'secondary' as const };
  }

  if (normalized === 'rejected' || normalized === 'duplicate') {
    return {
      label: normalized === 'duplicate' ? t('panel.statusDuplicate') : t('panel.statusRejected'),
      variant: 'destructive' as const,
    };
  }

  return { label: status, variant: 'outline' as const };
}

interface InvestigationRowProps {
  incident: IncidentListItem;
  onNavigate: (incidentId: string) => void;
  onSendToCirt: (incident: IncidentListItem) => void;
}

function InvestigationRow({ incident, onNavigate, onSendToCirt }: InvestigationRowProps) {
  const { t } = useTranslation('incidents');
  const { data: nis2Flag, isLoading } = useNIS2IncidentFlag({
    incidentId: incident.id,
  });
  const reportedForm = getReportedCirtForm(nis2Flag);

  return (
    <TableRow>
      <TableCell className="font-mono text-xs">{incident.incidentNumber}</TableCell>
      <TableCell className="min-w-[280px]">
        <button
          type="button"
          onClick={() => {
            onNavigate(incident.id);
          }}
          className="space-y-1 text-left"
        >
          <div className="font-medium">{incident.title}</div>
          <div className="text-sm text-muted-foreground">{truncate(incident.description, 120)}</div>
        </button>
      </TableCell>
      <TableCell>{resolveReporterName(incident, t)}</TableCell>
      <TableCell>
        <IncidentStatusBadge status={incident.status} size="sm" />
      </TableCell>
      <TableCell>
        {isLoading ? (
          <Badge variant="outline" className="gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t('panel.checking')}
          </Badge>
        ) : (
          <Badge variant={reportedForm ? 'secondary' : 'outline'}>{reportedForm ?? t('panel.none')}</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex flex-col items-end gap-2">
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onNavigate(incident.id);
              }}
            >
              {t('panel.open')}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                onSendToCirt(incident);
              }}
            >
              {t('panel.sendToCirt')}
            </Button>
          </div>
          {nis2Flag ? (
            <span className="text-xs text-muted-foreground">
              {t('panel.deadlines', {
                earlyWarning: formatDateTime(nis2Flag.earlyWarningDeadline),
                formalNotification: formatDateTime(nis2Flag.formalNotificationDeadline),
                finalReport: formatDateTime(nis2Flag.finalReportDeadline),
              })}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">
              {t('panel.chooseCirtForm')}
            </span>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function IncidentsPanelPage() {
  const { t } = useTranslation('incidents');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [cirtDialogIncident, setCirtDialogIncident] = useState<IncidentListItem | null>(null);

  const { incidents, isLoading, error, total, totalPages, refetch } = useIncidents({
    filters: DEFAULT_INCIDENT_FILTERS,
    page,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const { data: quickReportsData, isLoading: quickReportsLoading } = useQuickReports(20);

  const potentialIncidents = quickReportsData?.items ?? [];
  const incidentsUnderInvestigation = useMemo(
    () => incidents.filter((incident) => incident.status !== 'closed'),
    [incidents]
  );

  const handleNavigate = (incidentId: string) => {
    void navigate({ to: '/incidents/$incidentId', params: { incidentId } });
  };

  const handleSendToCirt = (incident: IncidentListItem) => {
    setCirtDialogIncident(incident);
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <h1 className="text-3xl font-semibold text-foreground">{t('panel.title')}</h1>
            <p className="max-w-3xl text-sm text-muted-foreground">
              {t('panel.subtitle')}
            </p>
            {/*
              TODO(WP4-B): Render <GroupScopePill /> once `useIncidents` exposes
              the rollup scope metadata returned by the backend
              `IncidentListResponse` (already inherits `RollupScopeMixin`).
              Skipped today because the frontend hook does not propagate those
              fields yet. See `docs/subsidiary-refactor/MIGRATION-GUIDE.md`.
            */}
          </div>
        </header>

        <RiskSecurityModuleContext module="incident-report" />

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <CardTitle>{t('panel.potentialIncidents')}</CardTitle>
                <CardDescription>
                  {t('panel.potentialIncidentsDesc')}
                </CardDescription>
              </div>
              <Link to="/surveys" search={{ tab: 'system' }}>
                <Button variant="outline">{t('panel.shareReportingForm')}</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {quickReportsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={index} className="h-14 w-full" />
                ))}
              </div>
            ) : potentialIncidents.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
                {t('panel.noPotentialIncidents')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('panel.potentialIncident')}</TableHead>
                      <TableHead>{t('panel.description')}</TableHead>
                      <TableHead>{t('panel.reportedBy')}</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>{t('panel.received')}</TableHead>
                      <TableHead className="text-right">{t('panel.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {potentialIncidents.map((report) => {
                      const status = normalizePotentialIncidentStatus(report.status, t);

                      return (
                        <TableRow key={report.id}>
                          <TableCell className="min-w-[220px] font-medium">
                            {derivePotentialIncidentName(report, t)}
                          </TableCell>
                          <TableCell className="min-w-[360px] text-sm text-muted-foreground">
                            {truncate(report.description, 150)}
                          </TableCell>
                          <TableCell>{report.reporterName}</TableCell>
                          <TableCell>
                            <Badge variant={status.variant}>{status.label}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDateTime(report.createdAt)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {report.hasAttachment && report.attachmentUrl ? (
                                <Button asChild size="sm" variant="outline">
                                  <a href={report.attachmentUrl}>{t('panel.attachment')}</a>
                                </Button>
                              ) : null}
                              <Link to="/incidents/new">
                                <Button size="sm">{t('panel.moveToInvestigation')}</Button>
                              </Link>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {error ? (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="mb-4 text-sm text-destructive">
              {error instanceof Error ? error.message : t('panel.failedToLoad')}
            </p>
            <Button variant="outline" onClick={() => void refetch()}>
              {t('panel.tryAgain')}
            </Button>
          </div>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <CardTitle>{t('panel.underInvestigation')}</CardTitle>
                  <CardDescription>
                    {t('panel.underInvestigationDesc')}
                  </CardDescription>
                </div>
                <Link to="/incidents/new">
                  <Button>
                    <FilePlus2 className="mr-2 h-4 w-4" />
                    {t('panel.addManually')}
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton key={index} className="h-14 w-full" />
                  ))}
                </div>
              ) : incidentsUnderInvestigation.length === 0 ? (
                <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
                  {t('panel.noUnderInvestigation')}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('panel.incidentId')}</TableHead>
                          <TableHead>{t('panel.incident')}</TableHead>
                          <TableHead>{t('panel.reportedBy')}</TableHead>
                          <TableHead>{t('panel.investigationStatus')}</TableHead>
                          <TableHead>{t('panel.reportedToCirt')}</TableHead>
                          <TableHead className="text-right">{t('panel.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {incidentsUnderInvestigation.map((incident) => (
                          <InvestigationRow
                            key={incident.id}
                            incident={incident}
                            onNavigate={handleNavigate}
                            onSendToCirt={handleSendToCirt}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                      {t('panel.showing', { current: incidentsUnderInvestigation.length, total })}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => {
                          setPage(page - 1);
                        }}
                      >
                        {t('panel.previous')}
                      </Button>
                      <span>
                        {t('panel.pageOf', { page, total: Math.max(totalPages, 1) })}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => {
                          setPage(page + 1);
                        }}
                      >
                        {t('panel.next')}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <CirtFormChooserDialog
        open={Boolean(cirtDialogIncident)}
        onOpenChange={(open) => {
          if (!open) {
            setCirtDialogIncident(null);
          }
        }}
        incidentId={cirtDialogIncident?.id ?? null}
        incidentLabel={
          cirtDialogIncident
            ? `${cirtDialogIncident.incidentNumber} - ${cirtDialogIncident.title}`
            : 'incident'
        }
      />
    </div>
  );
}
